<template>
  <div class="wc-sidebar">
    <!-- Header -->
    <div class="wc-sb-header">
      <div class="d-flex align-center" style="gap: 8px">
        <div class="wc-sb-icon-box">
          <v-icon x-small color="white">mdi-view-dashboard-outline</v-icon>
        </div>
        <span class="wc-sb-title">Vorlagen</span>
      </div>
      <v-btn icon x-small @click="$emit('create')" class="wc-sb-add">
        <v-icon x-small>mdi-plus</v-icon>
      </v-btn>
    </div>

    <div class="wc-sb-body custom-scrollbar">
      <!-- Drag hint -->
      <div class="wc-sb-hint">
        <v-icon x-small style="color: rgba(255, 255, 255, 0.15)" class="mr-1">mdi-gesture-swipe</v-icon>
        Drag auf Spalte zum Erstellen
      </div>

      <!-- Workflows -->
      <div class="wc-sb-section">
        <div class="wc-sb-label orange--text text--accent-3">
          <v-icon x-small color="orange accent-3" class="mr-1">mdi-lightning-bolt</v-icon> Workflows
        </div>
        <div
          v-for="tpl in workflowTemplates"
          :key="tpl.id"
          class="wc-sb-card"
          :style="{ '--card-color': tpl.color }"
          draggable="true"
          @dragstart="onDragStart($event, tpl)"
          @dragend="dragging = null">
          <div class="wc-sb-card-icon" :style="{ background: tpl.color + '20', color: tpl.color }">
            <v-icon x-small :color="tpl.color">{{ tpl.icon }}</v-icon>
          </div>
          <div class="wc-sb-card-info">
            <div class="wc-sb-card-name">{{ tpl.title }}</div>
            <div class="wc-sb-card-meta">{{ tpl.duration }} Min · Kunde</div>
          </div>
          <v-icon x-small class="wc-sb-drag-handle">mdi-drag-vertical</v-icon>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="wc-sb-section">
        <div class="wc-sb-label green--text text--accent-3"><v-icon x-small color="green accent-3" class="mr-1">mdi-flash</v-icon> Quick Actions</div>
        <div
          v-for="tpl in actionTemplates"
          :key="tpl.id"
          class="wc-sb-card"
          :style="{ '--card-color': tpl.color }"
          draggable="true"
          @dragstart="onDragStart($event, tpl)"
          @dragend="dragging = null">
          <div class="wc-sb-card-icon" :style="{ background: tpl.color + '20', color: tpl.color }">
            <v-icon x-small :color="tpl.color">{{ tpl.icon }}</v-icon>
          </div>
          <div class="wc-sb-card-info">
            <div class="wc-sb-card-name">{{ tpl.title }}</div>
            <div class="wc-sb-card-meta">{{ tpl.duration }} Min<span v-if="tpl.requiresCustomer"> · Kunde</span></div>
          </div>
          <v-icon x-small class="wc-sb-drag-handle">mdi-drag-vertical</v-icon>
        </div>
      </div>

      <!-- Custom Templates -->
      <div class="wc-sb-section" v-if="appointmentTemplates.length > 0">
        <div class="wc-sb-label" style="color: #42a5f5"><v-icon x-small color="#42A5F5" class="mr-1">mdi-calendar</v-icon> Eigene Vorlagen</div>
        <div
          v-for="tpl in appointmentTemplates"
          :key="tpl._id"
          class="wc-sb-card"
          :style="{ '--card-color': tpl.color || '#2196F3' }"
          draggable="true"
          @dragstart="onDragStart($event, tpl)"
          @dragend="dragging = null">
          <div class="wc-sb-card-icon" :style="{ background: (tpl.color || '#2196F3') + '20' }">
            <v-icon x-small :color="tpl.color || '#2196F3'">{{ tpl.icon || "mdi-calendar" }}</v-icon>
          </div>
          <div class="wc-sb-card-info">
            <div class="wc-sb-card-name">{{ tpl.title }}</div>
            <div class="wc-sb-card-meta">{{ tpl.duration }} Min<span v-if="tpl.requiresCustomer"> · Kunde</span></div>
          </div>
          <v-icon x-small class="wc-sb-drag-handle">mdi-drag-vertical</v-icon>
        </div>
      </div>

      <!-- Tasks -->
      <div class="wc-sb-section" v-if="taskTemplates.length > 0">
        <div class="wc-sb-label" style="color: #ab47bc">
          <v-icon x-small color="#AB47BC" class="mr-1">mdi-checkbox-marked</v-icon> Ämtli & Aufgaben
        </div>
        <div
          v-for="tpl in taskTemplates"
          :key="tpl._id"
          class="wc-sb-card"
          :style="{ '--card-color': tpl.color || '#9C27B0' }"
          draggable="true"
          @dragstart="onDragStart($event, tpl)"
          @dragend="dragging = null">
          <div class="wc-sb-card-icon" :style="{ background: (tpl.color || '#9C27B0') + '20' }">
            <v-icon x-small :color="tpl.color || '#9C27B0'">{{ tpl.icon || "mdi-checkbox-marked" }}</v-icon>
          </div>
          <div class="wc-sb-card-info">
            <div class="wc-sb-card-name">{{ tpl.title }}</div>
            <div class="wc-sb-card-meta">Aufgabe</div>
          </div>
          <v-icon x-small class="wc-sb-drag-handle">mdi-drag-vertical</v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WorkCalendarSidebar",
  props: {
    templates: { type: Array, default: () => [] },
  },
  data() {
    return {
      dragging: null,
      workflowTemplates: [
        {
          id: "wf1",
          title: "Probetraining",
          type: "appointment",
          specialType: "trial",
          duration: 90,
          color: "#FF9800",
          icon: "mdi-account-star",
          requiresCustomer: true,
        },
      ],
      actionTemplates: [
        {
          id: "act1",
          title: "Trainingsplan",
          type: "appointment",
          specialType: "workout",
          duration: 60,
          color: "#2196F3",
          icon: "mdi-dumbbell",
          requiresCustomer: true,
        },
        {
          id: "act2",
          title: "Anamnese (Einzeln)",
          type: "appointment",
          specialType: "anamnese",
          duration: 30,
          color: "#009688",
          icon: "mdi-clipboard-pulse",
          requiresCustomer: true,
        },
        {
          id: "act3",
          title: "Vertrag erstellen",
          type: "appointment",
          specialType: "new_contract",
          duration: 30,
          color: "#9C27B0",
          icon: "mdi-file-document-edit",
          requiresCustomer: false,
        },
        {
          id: "act4",
          title: "Vertrag / Upgrade",
          type: "appointment",
          specialType: "upgrade",
          duration: 30,
          color: "#AA00FF",
          icon: "mdi-star-circle",
          requiresCustomer: true,
        },
      ],
    }
  },
  computed: {
    appointmentTemplates() {
      return this.templates.filter((t) => t.type === "appointment")
    },
    taskTemplates() {
      return this.templates.filter((t) => t.type === "task")
    },
  },
  methods: {
    onDragStart(e, tpl) {
      this.dragging = tpl.id || tpl._id
      const dragObj = { ...tpl, create: true }
      e.dataTransfer.setData("templateObj", JSON.stringify(dragObj))
      e.dataTransfer.effectAllowed = "copy"
      this.$emit("drag-start", dragObj)
    },
  },
}
</script>

<style scoped>
.wc-sidebar {
  width: 280px;
  flex-shrink: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  background: var(--bg-sidebar);
  border-left: 1px solid var(--border-light);
}
.wc-sb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-medium);
}
.wc-sb-icon-box {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  display: flex;
  align-items: center;
  justify-content: center;
}
.wc-sb-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-main);
}
.wc-sb-add {
  color: var(--icon-muted) !important;
  transition: all 0.2s;
}
.wc-sb-add:hover {
  color: #66bb6a !important;
  background: rgba(76, 175, 80, 0.1) !important;
}
.wc-sb-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 10px;
}

/* Drag Hint */
.wc-sb-hint {
  font-size: 0.65rem;
  color: var(--text-disabled);
  text-align: center;
  padding: 6px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Sections */
.wc-sb-section {
  margin-bottom: 14px;
}
.wc-sb-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 4px 6px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.wc-sb-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  margin-bottom: 3px;
  border-radius: 10px;
  cursor: grab;
  background: var(--bg-panel);
  border: 1px solid transparent;
  transition: all 0.15s ease;
  position: relative;

  /* --- IPAD FIXES --- */
  -webkit-user-select: none; /* Verhindert Text-Markierung */
  user-select: none;
  -webkit-touch-callout: none; /* Verhindert das iOS Popup (Kopieren/Einfügen) */
  touch-action: none; /* Verhindert, dass die Seite beim Wischen scrollt */
}
.wc-sb-card:hover {
  background: var(--bg-panel-hover);
  border-color: var(--card-color);
  transform: translateX(3px);
}
.wc-sb-card:active {
  cursor: grabbing;
  transform: scale(0.96);
  opacity: 0.7;
}

/* Card Icon */
.wc-sb-card-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wc-sb-card-info {
  flex: 1;
  min-width: 0;
}
.wc-sb-card-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}
.wc-sb-card-meta {
  font-size: 0.7rem;
  color: var(--text-muted);
}

/* Drag Handle */
.wc-sb-drag-handle {
  color: var(--icon-muted) !important;
  opacity: 0.3 !important;
  transition: all 0.15s;
}
.wc-sb-card:hover .wc-sb-drag-handle {
  color: var(--icon-color) !important;
  opacity: 1 !important;
}

/* Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: var(--border-medium);
  border-radius: 3px;
}
</style>
